import React, { useState, useEffect, useCallback } from 'react';

function getToken() {
  return sessionStorage.getItem('wp_token');
}

export default function AdminInvitations() {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({ email: '', role: 'creator' });
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState(null);
  const [newToken, setNewToken] = useState(null);

  const fetchInvitations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/publishing/admin/invitations', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Failed to load invitations (${res.status})`);
      const data = await res.json();
      setInvitations(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const handleRevoke = async (inv) => {
    if (!window.confirm(`Revoke invitation for ${inv.email}?`)) return;
    try {
      const res = await fetch(`/api/publishing/admin/invitations/${inv.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error('Failed to revoke invitation');
      await fetchInvitations();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setFormError(null);
    setNewToken(null);

    if (!form.email.trim()) {
      setFormError('Email is required.');
      return;
    }

    setSending(true);
    try {
      const res = await fetch('/api/publishing/admin/invitations', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setNewToken(data.raw_token || data.token || null);
      setForm({ email: '', role: 'creator' });
      await fetchInvitations();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSending(false);
    }
  };

  const inviteUrl = newToken
    ? `https://wosspublishing.com/auth/activate?token=${newToken}`
    : null;

  const handleCopy = () => {
    if (inviteUrl) {
      navigator.clipboard.writeText(inviteUrl).catch(() => {});
    }
  };

  return (
    <>
      <h1 className="wp-portal__page-title">Invitations</h1>
      <p className="wp-portal__page-sub">Send and manage portal invitations.</p>

      {newToken && (
        <div className="wp-portal__token-reveal">
          <p className="wp-portal__token-reveal-heading">
            Copy this invitation link now — it will not be shown again.
          </p>
          <code className="wp-portal__token-reveal-url">{inviteUrl}</code>
          <button
            className="wp-portal__btn wp-portal__btn--sm wp-portal__btn--ghost"
            onClick={handleCopy}
          >
            Copy to Clipboard
          </button>
        </div>
      )}

      <div className="wp-portal__form-section">
        <h2 className="wp-portal__form-section-title">Send Invitation</h2>
        <form className="wp-portal__form" onSubmit={handleSend} noValidate>
          {formError && (
            <div className="wp-portal__error wp-portal__error--form">{formError}</div>
          )}
          <div className="wp-portal__field">
            <label className="wp-portal__label" htmlFor="inv-email">
              Email <span className="wp-portal__required">*</span>
            </label>
            <input
              id="inv-email"
              className="wp-portal__input"
              type="email"
              value={form.email}
              onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
              placeholder="user@example.com"
            />
          </div>
          <div className="wp-portal__field">
            <label className="wp-portal__label" htmlFor="inv-role">Role</label>
            <select
              id="inv-role"
              className="wp-portal__input wp-portal__select"
              value={form.role}
              onChange={e => setForm(prev => ({ ...prev, role: e.target.value }))}
            >
              <option value="creator">Creator</option>
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="wp-portal__form-actions">
            <button
              type="submit"
              className="wp-portal__btn wp-portal__btn--primary"
              disabled={sending}
            >
              {sending ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>

      <div className="wp-portal__form-section">
        <h2 className="wp-portal__form-section-title">Sent Invitations</h2>

        {loading && <p className="wp-portal__page-sub">Loading...</p>}

        {!loading && error && (
          <div className="wp-portal__error">{error}</div>
        )}

        {!loading && !error && invitations.length === 0 && (
          <div className="wp-portal__empty">
            <p className="wp-portal__empty-label">No invitations sent.</p>
          </div>
        )}

        {!loading && !error && invitations.length > 0 && (
          <div className="wp-portal__table-wrap">
            <table className="wp-portal__table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th>Invited By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map(inv => (
                  <tr key={inv.id}>
                    <td>{inv.email}</td>
                    <td>{inv.role}</td>
                    <td>
                      <span className={`wp-portal__badge wp-portal__badge--${inv.status}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td>
                      {inv.expires_at
                        ? new Date(inv.expires_at).toLocaleDateString()
                        : '—'}
                    </td>
                    <td>{inv.invited_by_name || inv.invited_by || '—'}</td>
                    <td className="wp-portal__actions">
                      {inv.status === 'pending' && (
                        <button
                          className="wp-portal__btn wp-portal__btn--sm wp-portal__btn--danger"
                          onClick={() => handleRevoke(inv)}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
